import { createReducer, on } from '@ngrx/store';
import { authApiActions } from '@core/auth/auth.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { defaultEntityState, IEntityState } from '@core/state/entities/entity';
import {
  removeEntities,
  removeEntitiesByFilter,
} from '@core/state/entities/entity.helpers';
import { entityKey } from '@core/state/entities/entity-name-map';
import { IBulkStudyUpdateJob } from '@core/state/entities/bulk-study-update-job/bulk-study-update-job.entity';
import {
  bulkStudyUpdateJobFamily,
  fenceDeletedProject,
  ProjectJobFamilyState,
  reconcileProjectJobSnapshot,
  resetProjectJobSnapshotVersions,
} from '@core/state/entities/project-job-snapshot';

type BulkStudyUpdateJobState = ProjectJobFamilyState<IBulkStudyUpdateJob>;

/**
 * Bulk study update jobs are server-owned and read-only on the client. Whole-Project snapshots are
 * reconciled against the Project audit version (an older snapshot may never remove a job a newer
 * one created), and the jobs they carry are merged by the server-owned per-job revision.
 */
export const bulkStudyUpdateJobReducer = createReducer(
  defaultEntityState<IBulkStudyUpdateJob>(),
  on(
    projectDetailActions.created,
    projectDetailActions.detailLoaded,
    projectDetailActions.projectForInvitationRetrieved,
    projectDetailActions.requestToJoinProjectResponseReceivedAndGranted,
    projectDetailActions.projectUpdated,
    (state, props): IEntityState<IBulkStudyUpdateJob> =>
      reconcileProjectJobSnapshot(
        bulkStudyUpdateJobFamily,
        state as BulkStudyUpdateJobState,
        props
      )
  ),
  on(
    projectDetailActions.registered,
    (state, props): IEntityState<IBulkStudyUpdateJob> =>
      reconcileProjectJobSnapshot(
        bulkStudyUpdateJobFamily,
        state as BulkStudyUpdateJobState,
        props
      )
  ),
  on(
    authApiActions.startUserImpersonation,
    authApiActions.stopUserImpersonation,
    (state): IEntityState<IBulkStudyUpdateJob> =>
      resetProjectJobSnapshotVersions(state as BulkStudyUpdateJobState)
  ),
  on(
    projectDetailActions.projectDeleted,
    (state, props): IEntityState<IBulkStudyUpdateJob> => {
      const bulkState = state as BulkStudyUpdateJobState;
      return fenceDeletedProject(
        bulkState,
        removeEntitiesByFilter(
          bulkState,
          (projChild) => projChild.projectId === props.projectId
        ) as BulkStudyUpdateJobState,
        props.projectId,
        props.terminalPhysicalDeletion
      );
    }
  ),
  on(
    projectDetailActions.deleteBulkStudyUpdateJob,
    projectDetailActions.bulkStudyUpdateJobCancelled,
    (state, props): IEntityState<IBulkStudyUpdateJob> =>
      removeEntities(entityKey.bulkStudyUpdateJobs, state, props)
  )
);
